import { useCallback } from 'react';

import { TaxonomyType } from '@revelio/data-access';
import { RadioGroup } from '@revelio/ui';

import { applyGeographyTaxonomyPreference } from '../../../next/CustomDash/filters/utils/applyGeographyTaxonomyPreference';
import { useTaxonomyList } from '../../selection-lists/hooks/useTaxonomyList';
import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import { UserPreferenceKey } from '../types';
import { resolveGeographyPreference } from '../utils/geography-preference';
import { GeographyTaxonomyOption } from './GeographyTaxonomyOption';
import layoutStyles from './geography-editor.module.css';
import styles from './preference-editors.module.css';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.Geography,
  label: 'Geography',
};

export default function GeographyEditor({
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  const { taxonomies } = useTaxonomyList({
    taxonomyType: TaxonomyType.Geography,
  });

  // Derived from props, so computed rather than mirrored in state: the
  // useState/useEffect pair it replaces re-rendered once more on every
  // preference change and could show a stale radio for that frame.
  const taxonomyId = resolveGeographyPreference(
    preferences[UserPreferenceKey.Geography]
  ).taxonomyId;

  const onUpdate = useCallback(
    (nextId: number) => {
      const selected = taxonomies.find((taxonomy) => taxonomy.id === nextId);
      const value = {
        taxonomyId: nextId,
        ...(selected?.version ? { version: selected.version } : {}),
      };
      // Both preference parts, via the one helper every switch surface shares
      // — the taxonomies share no ids, so the GEOGRAPHY-scope defaults have to
      // be regenerated alongside the choice or the page lands on a primary
      // filter that selects nothing.
      applyGeographyTaxonomyPreference(nextId, selected?.version);

      // Dispatched after the storage writes so taxonomy-dependent filter
      // transforms read the latest values.
      onPreferenceUpdate?.(UserPreferenceKey.Geography, value);
    },
    [onPreferenceUpdate, taxonomies]
  );

  if (taxonomies.length === 0) return null;

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Geography"
        indicateDiff={diff?.includes(meta.id) ?? false}
        tooltip="The geography boundaries used for filtering and map visualization."
      />
      <RadioGroup
        value={String(taxonomyId)}
        onChange={(val) => onUpdate(Number(val))}
        className={styles.radioGroup}
      >
        <div className={layoutStyles.taxonomyList}>
          {taxonomies.map((taxonomy) => (
            <GeographyTaxonomyOption
              key={taxonomy.id}
              taxonomyId={taxonomy.id}
              name={taxonomy.name}
              version={taxonomy.version}
              disabled={false}
            />
          ))}
        </div>
      </RadioGroup>
    </>
  );
}
